import { SurveyPage } from '@quality24/inpatient-typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Heading, Text, TextArea } from '@quality24/design-system';
import ImprovementsSelector from '@/inpatient-patient-pwa/molecules/ImprovementsSelector';
import SmileyInputField from '@/inpatient-patient-pwa/molecules/SmileyInputField';

import { SurveyHook } from '@/inpatient-patient-pwa/hooks/useSurvey';
import useImprovements from '../useImprovements';

export interface Props {
  page: Pick<
    SurveyPage,
    'id' | 'category' | 'type' | 'titleI18N' | 'descriptionI18N' | 'data'
  >;
  answer: SurveyHook['answer'];
}

const DefaultQuestionPage: React.FunctionComponent<Props> = ({
  page,
  answer,
}) => {
  const { t } = useTranslation();
  const description = page.descriptionI18N;
  const improvements = useImprovements(page.data);

  const showImprovementsSelector =
    answer.values.value.smileyScore !== undefined &&
    answer.values.value.smileyScore < 5;

  return (
    <>
      <Heading as="h2" weight="semiBold" size="md">
        {page.titleI18N}
      </Heading>
      <Text className="mb-4" style={{ whiteSpace: 'pre-wrap' }}>
        {description}
      </Text>

      <SmileyInputField
        className="mb-5"
        value={answer.values.value.smileyScore}
        onChange={(v: number) => {
          answer.setFieldValue('value.smileyScore', v);
          if (v === 5) {
            answer.setFieldValue('value.improvements', undefined);
          }
        }}
      />
      {improvements.titles.length > 0 && showImprovementsSelector && (
        <ImprovementsSelector
          className="my-4"
          titles={improvements.titles}
          value={improvements.toDisplayed(
            answer.values.value.improvements || [],
          )}
          onChange={(v) =>
            answer.setFieldValue('value.improvements', improvements.toStored(v))
          }
        />
      )}
      <TextArea
        id="formFeedback"
        className="my-4"
        label={t('survey.comment.label')}
        name="text"
        value={answer.values.value.text}
        errorText={answer.errors?.value?.text}
        onChange={(e) => answer.setFieldValue('value.text', e.target.value)}
      />
    </>
  );
};

export default DefaultQuestionPage;
